import { newMilestoneId, rewardFieldPreview } from "../../utils/passUtils";
import { readableTextColor } from "./imaging";
import { DEFAULT_DISCOUNT, DEFAULT_STAMP_COUNT } from "./presets";

// Turns the /start Customize answers into the exact card data the dashboard's
// NewPass saves. The backend stores this JSON verbatim and applies no defaults,
// and three other readers depend on its keys: the wallet builders (a missing
// header/left/right field makes an invalid .pkpass), the scanner app (reads
// `preview`), and the dashboard's EditPass (re-opens the card for editing). So
// the shape below mirrors NewPass's defaults and PassForm.changeType per type.

const ONE_YEAR_AHEAD = () =>
  new Date(new Date().setMonth(new Date().getMonth() + 12)).toISOString().slice(0, 10);

// Right-hand field and starting balance per type (PassForm.changeType).
const TYPE_DEFAULTS = {
  stamp: { rightField: "rewards", initialPoints: 2 },
  cashback: { rightField: "points", initialPoints: 200 },
  cashback_money: { rightField: "balance_money", initialPoints: 0 },
  balance: { rightField: "balance", initialPoints: 0 },
  discount: { rightField: "discount", initialPoints: 0 },
  membership: { rightField: "visits", initialPoints: 0 },
  coupon: { initialPoints: 1 },
};

export function stampMilestones({ stampCount, reward, midReward }) {
  const count = Number(stampCount) || DEFAULT_STAMP_COUNT;
  const milestones = [{ position: count, label: String(reward || "").trim(), _uid: newMilestoneId() }];
  if (midReward?.enabled) {
    const position = Math.min(Math.max(1, Number(midReward.position) || 1), count - 1);
    milestones.unshift({ position, label: String(midReward.label || "").trim(), _uid: newMilestoneId() });
  }
  return milestones;
}

// Instagram handle or URL → a tappable https URL.
export function instagramUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const handle = raw.replace(/^@/, "").replace(/^(www\.)?instagram\.com\//i, "").replace(/\/+$/, "");
  return handle ? `https://instagram.com/${handle}` : "";
}

export function normalizeUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

// Back of the card. Apple Wallet gives every `links[]` entry the same field key,
// and a pass with duplicate keys can be rejected, so only the first link goes to
// links[]; any other link becomes a back field (iOS still makes the URL
// tappable). Rows the merchant left off or empty are not sent.
export function backOfCard(back = {}, t) {
  const linkRows = [];
  if (back.booking?.enabled && back.booking.value?.trim()) {
    linkRows.push({ label: back.booking.label?.trim() || t("start.back.booking.defaultLabel"), url: normalizeUrl(back.booking.value) });
  }
  if (back.instagram?.enabled && back.instagram.value?.trim()) {
    linkRows.push({ label: "Instagram", url: instagramUrl(back.instagram.value) });
  }

  const customBackFields = [];
  if (back.hours?.enabled && back.hours.value?.trim()) {
    customBackFields.push({ label: t("start.back.hours.label"), value: back.hours.value.trim() });
  }
  if (back.info?.enabled && (back.info.label?.trim() || back.info.value?.trim())) {
    customBackFields.push({ label: back.info.label?.trim() || "", value: back.info.value?.trim() || "" });
  }
  linkRows.slice(1).forEach((row) => customBackFields.push({ label: row.label, value: row.url }));

  const links = linkRows.slice(0, 1);
  return {
    enableLinks: links.length > 0,
    links: links.length ? links : [{ label: "", url: "" }],
    enableCustomBackFields: customBackFields.length > 0,
    customBackFields: customBackFields.length ? customBackFields : [{ label: "", value: "" }],
  };
}

// Port of NewPass's updatePreview: the label/value the card shows in each slot.
export function buildPreviewFields(formData, { t, wl, memberName = "", currency = "" }) {
  const points = formData.initialPoints;
  const header = {
    none: { label: "", value: "" },
    rewards: { label: wl.rewards, value: rewardFieldPreview(points, formData.milestones, t) },
    stamps: { label: wl.stamps, value: points ?? "" },
    rewards_stamps: { label: wl.stamps, value: points },
    points: { label: wl.points, value: points },
    balance: { label: wl.balance, value: points },
    balance_money: { label: wl.balance_money, value: `${currency} ${points}` },
    visits: { label: wl.visits, value: points },
    tier: { label: formData.tier?.label || wl.tier, value: formData.tier?.value || "" },
    valid_until: { label: wl.valid_until, value: formData.formattedValidUntil || "" },
    discount: { label: wl.discount, value: `${formData.discount}%` },
  };
  const secondary = {
    none: { label: "", value: "" },
    member: { label: wl.member, value: memberName },
    birthday: { label: wl.birthday, value: "01/01/1990" },
    rewards: { label: wl.rewards, value: rewardFieldPreview(points, formData.milestones, t) },
    stamps: { label: wl.stamps, value: points ?? "" },
    valid_until: { label: wl.valid_until, value: formData.formattedValidUntil || "" },
    tier: { label: formData.tier?.label || wl.tier, value: formData.tier?.value || "" },
    balance: { label: wl.balance, value: points ?? "" },
    balance_money: { label: wl.balance_money, value: `${currency} ${points}` },
    visits: { label: wl.visits, value: points || "0" },
    points: { label: wl.points, value: points ?? "" },
    discount: { label: wl.discount, value: `${formData.discount}%` },
  };
  const slot = (table, key, fallback) => {
    const entry = table[key];
    if (entry) return { label: entry.label, value: String(entry.value ?? "") };
    return fallback;
  };
  const rightField =
    formData.type === "coupon"
      ? { label: formData.campaign?.label || "", value: formData.campaign?.value || "" }
      : slot(secondary, formData.rightField, { label: formData.rightField, value: "{Response}" });
  return {
    headerField: slot(header, formData.headerField, { label: formData.headerField, value: "{Response}" }),
    leftField: slot(secondary, formData.leftField, { label: formData.leftField, value: "{Response}" }),
    rightField,
  };
}

// The full card data, NewPass-shaped. `logoUrl` is what the preview shows (the
// store's saved logo, or the local one before it is uploaded).
export function buildPassFormData(draft, { t, wl, user, store, logoUrl, stampIconUrl }) {
  const type = draft.cardType || "stamp";
  const typeDefaults = TYPE_DEFAULTS[type] || TYPE_DEFAULTS.stamp;
  const backgroundColor = draft.cardColor;
  const textColor = readableTextColor(backgroundColor);
  const memberName = user?.displayName || "";
  const couponLabel = t("start.card.coupon.label");
  const couponValue = String(draft.couponOffer || "").trim();

  const formData = {
    name: t("start.card.passName", { store: draft.storeName?.trim() || store?.name || "" }).slice(0, 60),
    type,
    company: draft.storeName?.trim() || store?.name || "",
    // null, not "": an empty src makes the browser fetch the page again.
    companyLogo: logoUrl || store?.logo_url || null,
    backgroundColor,
    secondaryColor: textColor,
    textColor,
    backgroundImage: null,
    displayName: memberName,
    barcodeType: "qr",
    supportEmail: user?.email || "",
    initialPoints: typeDefaults.initialPoints,
    stampCount: Number(draft.stampCount) || DEFAULT_STAMP_COUNT,
    stampRewardMode: "milestone",
    milestones:
      type === "stamp"
        ? stampMilestones(draft)
        : [{ position: Number(draft.stampCount) || DEFAULT_STAMP_COUNT, label: String(draft.reward || "").trim(), _uid: newMilestoneId() }],
    validUntilDateFormat: "en-GB",
    validUntil: ONE_YEAR_AHEAD(),
    formattedValidUntil: "",
    enableTier: false,
    tier: { label: "", value: "Bronze" },
    campaign:
      type === "coupon"
        ? { label: couponLabel, value: couponValue }
        : { label: "Reward", value: "Free Coffee" },
    targetPassId: "none",
    targetAmount: type === "stamp" ? null : "",
    applyInitialBonusOnConversion: false,
    stampIcon: stampIconUrl || null,
    stampIconColor: backgroundColor,
    useCustomTerms: false,
    termsOfServiceUrl: "",
    enableCustomField: false,
    customField: null,
    selectedCustomFieldId: null,
    selectedCustomField: null,
    passExpirationType: "fixed",
    passExpirationDynamic: 1,
    passExpirationDynamicUnit: "years",
    pointsExpirationType: "dynamic",
    pointsExpirationDynamic: 1,
    pointsExpirationDynamicUnit: "years",
    pointsValidUntil: ONE_YEAR_AHEAD(),
    enablePassExpiration: false,
    enablePointsExpiration: false,
    headerField: "none",
    leftField: "member",
    rightField: type === "coupon" ? couponLabel : typeDefaults.rightField,
    discount: type === "discount" ? Number(draft.discount) || DEFAULT_DISCOUNT : 5,
    enableReviewFeedbackLink: false,
    reviewFeedback: { label: "Review & Feedback", url: "" },
    enableReferalProgram: false,
    referralProgram: { type: "use", referrerPoints: 0, refereePoints: 0 },
    ...backOfCard(draft.back, t),
    // Marks cards made by /start: the backend keeps unknown keys as they are, so
    // this survives on the server for analysis and is ignored by every reader.
    // The answers that are not visible in the card itself, so the screen can
    // be rebuilt as the merchant left it on another phone or after sign-out.
    onboarding: {
      flow: "start",
      businessType: draft.businessType || "other",
      logoKind: draft.logoKind || "monogram",
      iconId: draft.iconId || null,
      back: draft.back || null,
    },
  };

  formData.preview = buildPreviewFields(formData, {
    t,
    wl,
    memberName,
    currency: draft.currency || store?.currency || "",
  });
  return formData;
}

// What the backend needs before it will accept the card. Mirrors the dashboard
// validators plus the campaign checks that otherwise come back as a 422
// ("Campaign: Description can't be blank").
export function cardProblems(draft) {
  const problems = {};
  const type = draft.cardType || "stamp";
  if (type === "stamp") {
    if (!String(draft.reward || "").trim()) problems.reward = "required";
    if (draft.midReward?.enabled && !String(draft.midReward.label || "").trim()) {
      problems.midReward = "required";
    }
  }
  if (type === "coupon" && !String(draft.couponOffer || "").trim()) problems.couponOffer = "required";
  if (type === "discount") {
    const d = Number(draft.discount);
    if (!Number.isFinite(d) || d <= 0 || d > 100) problems.discount = "invalid";
  }
  return problems;
}

// The parts of the card that change the strip images. When none of them change
// an edit can be saved without regenerating and re-uploading the images.
export function stripSignature(formData) {
  return JSON.stringify([
    formData.type,
    formData.stampCount,
    formData.textColor,
    formData.backgroundColor,
    formData.stampIconColor,
    formData.stampIcon ? formData.stampIcon.length : 0,
    formData.stampIcon ? formData.stampIcon.slice(-64) : "",
  ]);
}

// The JSON that is sent. Drops what only the preview needs: the local logo
// (a data URL would bloat the stored JSON) and the stamp icon (uploaded as a
// file, exactly like NewPass).
export function passLayoutForSave(formData, store) {
  // eslint-disable-next-line no-unused-vars
  const { stampIcon: _stampIcon, ...rest } = formData;
  return {
    ...rest,
    companyLogo: store?.logo_url || "",
    // eslint-disable-next-line no-unused-vars
    milestones: (formData.milestones || []).map(({ _uid, ...m }) => m),
  };
}
